import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, countOf, treatsSeveral, tweensAxis } from '../skillUsage';

export const panBetweenStationsSkill: CodeoSkill = {
    name: SKILL_NAMES.panBetweenStations,
    kind: 'codeo',
    description:
        'Lay content out on a canvas wider than the frame and move the camera between fixed stops ' +
        '— for two or three related points that live in one space rather than replacing each other.',
    checkLooksFor: 'the canvas tweened on x, to more than one station',
    documentUsesSkill: check(
        (script) =>
            tweensAxis(script, 'x') &&
            (treatsSeveral(script) || countOf(script, /\bx\s*(?::|[,}])/g) >= 2),
    ),
    instructions: `# Pan Between Stations

Everything exists at once on a canvas larger than the frame, and the camera travels between fixed stations, holding at each. Because nothing is created or destroyed, the viewer builds a mental map: these things are *next to each other*, and the relationship between them is part of the argument.

It is the alternative to swapping states. Use it when the points are siblings — three regions, three stages of a pipeline, a before and an after that genuinely coexist.

## Markup

Author the whole world at final scale, positioned along one axis. The camera is the wrapper.

\`\`\`html
<div id="viewport" style="position: absolute; inset: 0; overflow: hidden;">
  <div id="world" data-l5-allow-offscreen style="position: absolute; left: 0; top: 0; width: 5760px; height: 1080px;">
    <div style="position: absolute; left: 300px; top: 430px; width: 1320px; font-family: var(--l5-font-0), sans-serif; font-size: 104px; font-weight: 800; color: var(--l5-color-0, #101820);">Capture anywhere</div>
    <div style="position: absolute; left: 2220px; top: 430px; width: 1320px; font-family: var(--l5-font-0), sans-serif; font-size: 104px; font-weight: 800; color: var(--l5-color-0, #101820);">Edit together</div>
    <div style="position: absolute; left: 4140px; top: 430px; width: 1320px; font-family: var(--l5-font-0), sans-serif; font-size: 104px; font-weight: 800; color: var(--l5-color-0, #101820);">Publish once</div>
    <div id="rail" style="position: absolute; left: 300px; top: 700px; width: 5160px; height: 5px; background: var(--l5-color-1, #d6dbe3);"></div>
  </div>
</div>
\`\`\`

The rail running the full width is what makes the stations read as one continuous space rather than three frames that happen to be adjacent.

## Timeline code

Each leg is one tween with a hold between. The travel must be quicker than the holds, or the piece is mostly transit.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const STATIONS = [0, -1920, -3840];
const HOLD = 1.8;
const TRAVEL = 0.85;

gsap.set('#world', { x: STATIONS[0] });

STATIONS.slice(1).forEach((x, index) => {
    tl.to('#world', { x, duration: TRAVEL, ease: 'power2.inOut' }, HOLD + index * (HOLD + TRAVEL));
});

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Axis**: horizontal reads as a sequence or a journey; vertical reads as a hierarchy or a descent into detail.
- **Scale as well as pan**: zooming slightly out during travel and back in on arrival reads as a real camera and makes long legs less monotonous.
- **Diagonal layout**: stations offset on both axes read as a map rather than a strip, which suits geography or a non-linear relationship.
- **Two stations only**: the simplest and often the best — a before and an after that the camera moves between rather than cutting.
- **Ambient drift under it**: the holds are where the frame can die; a slow drift on the world keeps them alive — see \`${SKILL_NAMES.driftAndHold}\`.
- **Rail as progress**: marking the stations on the rail and lighting each as it is reached gives the viewer a position in the sequence.

## Pitfalls

- The world is \`x\`-tweened, so it must not carry a CSS \`transform\`; station positions live in \`left\`.
- The world reaches far outside the frame by definition, so it needs \`data-l5-allow-offscreen\`.
- Station offsets must be exact multiples of the frame width, or a station lands off-centre and the composition looks accidental.
- Three stations is the ceiling for a single scene. Each needs a real hold, and the arithmetic runs out of time fast.
- Don't animate content at a station while the camera is still travelling. Let the camera arrive, then let the station act.
- A 5760px-wide world is 3× the rasterizable area. Keep the node count low and avoid blurs or shadows spanning the full width.
- If the points genuinely replace each other rather than coexisting, this is the wrong technique — see \`${SKILL_NAMES.sharedAxisSlide}\`.`,
};
